import {
  Box,
  Checkbox,
  FormControlLabel,
  Radio,
  Typography
} from '@mui/material'
import DOMPurify from 'dompurify'
import debounce from 'lodash.debounce'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import {
  QUESTION_CONTENT_TYPE_MAP,
  QUESTION_SUB_TYPE_MAP
} from '../../utils/constant'
import MarkdownRenderer from '../markdown'
import CustomTextField from '../textfield'
import SanitizedHTMLOptionItem from './SanitizedHTMLOptionItem'

const ComprehensionShortAnswer = memo(
  ({ subQ, subIndex, handleOptionSelected }) => {
    const [textValue, setTextValue] = useState(subQ?.textResponse || '')

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedSave = useCallback(
      debounce((id, val, idx) => {
        handleOptionSelected(id, null, val, idx)
      }, 500),
      [handleOptionSelected]
    )

    useEffect(() => {
      setTextValue(subQ?.textResponse || '')
    }, [subQ?.textResponse])

    const onChange = useCallback(
      (e) => {
        const val = e.target.value
        setTextValue(val)
        debouncedSave(subQ?.id, val, subIndex)
      },
      [debouncedSave, subQ?.id, subIndex]
    )

    return (
      <Box className="short-question-container">
        <CustomTextField
          fullWidth
          multiline
          minRows={4}
          placeholder="Type your answer here..."
          value={textValue}
          onChange={onChange}
          inputProps={{
            ...(subQ?.maxCharacters != null && {
              maxLength: subQ?.maxCharacters
            })
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              padding: '12px'
            }
          }}
        />
        {subQ?.maxCharacters != null && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: '4px',
              color: 'text.secondary'
            }}
          >
            <Typography variant="caption">
              {textValue?.length || 0} / {subQ?.maxCharacters || 2000}{' '}
              Characters
            </Typography>
          </Box>
        )}
      </Box>
    )
  }
)

const MCQTypeRenderer = ({
  question,
  questionNo,
  usedForEvaluation,
  readOnly = true,
  handleOptionSelected,
  isPsychometric = false
}) => {
  // Memo
  const questionId = useMemo(() => question?.id, [question?.id])
  const subType = useMemo(() => question?.subtype, [question?.subtype])
  const options = useMemo(() => question?.options, [question?.options])

  const contentType = useMemo(
    () => question?.contentType,
    [question?.contentType]
  )

  const questionContent = useMemo(() => {
    return question?.content
      ? question?.contentType === QUESTION_CONTENT_TYPE_MAP.HTML
        ? DOMPurify.sanitize(question?.content)
        : question?.content
      : ''
  }, [question?.content, question?.contentType])

  const textResponse = useMemo(
    () => question?.textResponse,
    [question?.textResponse]
  )

  const aiSummary = useMemo(() => question?.feedBack, [question?.feedBack])

  const isMultiChoice = useMemo(
    () => question?.isMultiChoice,
    [question?.isMultiChoice]
  )

  return (
    <>
      {contentType === QUESTION_CONTENT_TYPE_MAP.MARKDOWN ? (
        <MarkdownRenderer value={questionContent} />
      ) : contentType === QUESTION_CONTENT_TYPE_MAP.HTML ? (
        <Typography
          className="main-question html-markdown-table-and-list-styles"
          dangerouslySetInnerHTML={{ __html: questionContent }}
          sx={{ whiteSpace: 'pre-wrap' }}
        />
      ) : (
        <Typography
          className="main-question html-markdown-table-and-list-styles"
          sx={{ whiteSpace: 'pre-wrap' }}
        >
          {questionContent}
        </Typography>
      )}

      {subType === QUESTION_SUB_TYPE_MAP.SHORT_ANSWER &&
        textResponse &&
        readOnly && (
          <Box className="questions-option-list-container">
            <Box className="candidate-response-card">
              <Box>
                <Box className="candidate-response-header">
                  <Typography className="candidate-response-title">
                    Candidate Response
                  </Typography>
                </Box>
                <Box className="candidate-response-body">
                  <Typography
                    variant="body1"
                    className="candidate-response-text"
                    sx={{ whiteSpace: 'pre-wrap' }}
                  >
                    {textResponse}
                  </Typography>
                </Box>
              </Box>
              {aiSummary && (
                <Box>
                  <Box className="candidate-response-header">
                    <Typography className="candidate-response-title">
                      AI Summary
                    </Typography>
                  </Box>
                  <Box className="candidate-response-body">
                    <Typography
                      variant="body1"
                      className="candidate-response-text"
                      sx={{ whiteSpace: 'pre-wrap' }}
                    >
                      {aiSummary}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        )}

      {subType === QUESTION_SUB_TYPE_MAP.SHORT_ANSWER && !readOnly && (
        <ComprehensionShortAnswer
          subQ={question}
          subIndex={questionNo}
          handleOptionSelected={handleOptionSelected}
        />
      )}

      {subType === QUESTION_SUB_TYPE_MAP.MCQ && (
        <Box className="questions-option-list-container">
          {options?.map((option, optionIndex) => {
            const isSelected = question?.optionsIds?.includes(option?.id)

            return (
              <Box
                className={`html-markdown-table-and-list-styles ${
                  isPsychometric
                    ? option?.isSelected
                      ? 'correct-option-for-evaluation'
                      : 'option-for-evaluation'
                    : usedForEvaluation && option?.isCorrect
                      ? 'correct-option-for-evaluation'
                      : 'option-for-evaluation'
                } option-item-container ${
                  isPsychometric
                    ? 'option-for-evaluation'
                    : usedForEvaluation && option?.isSelected && !option?.isCorrect
                      ? 'incorrect-option-for-evaluation'
                      : 'option-for-evaluation'
                }`}
                key={`${questionId}-${option?.id || optionIndex}`}
              >
                <FormControlLabel
                  label={
                    contentType === QUESTION_CONTENT_TYPE_MAP.PLAIN_TEXT ? (
                      option?.content
                    ) : contentType === QUESTION_CONTENT_TYPE_MAP.HTML ? (
                      <SanitizedHTMLOptionItem option={option} />
                    ) : (
                      <MarkdownRenderer value={option?.content} />
                    )
                  }
                  sx={{
                    cursor: readOnly
                      ? 'default'
                      : handleOptionSelected
                        ? 'cursor'
                        : 'default',
                    ...(contentType === QUESTION_CONTENT_TYPE_MAP.PLAIN_TEXT
                      ? { whiteSpace: 'pre-wrap' }
                      : {})
                  }}
                  control={
                    isMultiChoice ? (
                      <Checkbox
                        sx={{
                          cursor: 'default',
                          pointerEvents: 'none'
                        }}
                        readOnly={readOnly}
                        checked={
                          usedForEvaluation
                            ? option?.isSelected
                            : isSelected || option?.isCorrect || false
                        }
                        onChange={
                          readOnly
                            ? () => {}
                            : (e) => {
                                let selectedOptions = [
                                  ...(question?.optionsIds || [])
                                ]

                                if (e?.target?.checked) {
                                  selectedOptions.push(option?.id)
                                } else {
                                  selectedOptions = selectedOptions.filter(
                                    (id) => id !== option?.id
                                  )
                                }

                                handleOptionSelected(
                                  question?.id,
                                  selectedOptions,
                                  null,
                                  questionNo
                                )
                              }
                        }
                      />
                    ) : (
                      <Radio
                        sx={{
                          cursor: 'default',
                          pointerEvents: 'none'
                        }}
                        // readOnly={readOnly}
                        checked={
                          usedForEvaluation
                            ? option?.isSelected
                            : isSelected || option?.isCorrect || false
                        }
                        onChange={
                          readOnly
                            ? () => {}
                            : (e) => {
                                if (e?.target?.checked) {
                                  handleOptionSelected(
                                    question?.id,
                                    [option?.id],
                                    null,
                                    questionNo
                                  )
                                }
                              }
                        }
                      />
                    )
                  }
                />
              </Box>
            )
          })}
        </Box>
      )}
    </>
  )
}

export default memo(MCQTypeRenderer)
